import { useState, useEffect, useCallback, useRef } from 'react';
import deviceDataService from '../services/deviceDataService';

const CACHE_DURATION = parseInt(process.env.REACT_APP_CACHE_DURATION) || 300000; // 5 minutes default

/**
 * Unified loading configuration for device data panels
 * This configuration drives phase priorities and panel behavior.
 */
const LOADING_CONFIG = {
  phases: [
    {
      id: 'deviceHealth',
      name: 'Device Health',
      priority: 1,
      sequential: false,
      panels: [
        {
          key: 'deviceHealth',
          displayName: 'Device Health',
          apiMethod: 'getDeviceHealthAggregate',
          required: false,
          updateMethod: 'updateDeviceHealthAggregate'
        }
      ]
    },
    {
      id: 'networkHealth',
      name: 'Network Health',
      priority: 3,
      sequential: false,
      panels: [
        {
          key: 'networkHealth',
          displayName: 'Network Health',
          apiMethod: 'getNetworkHealthAggregate',
          required: false,
          updateMethod: 'updateNetworkHealth'
        }
      ]
    },
    {
      id: 'deviceInfo',
      name: 'Device Information',
      priority: 2,
      sequential: false,
      panels: [
        {
          key: 'deviceInfo',
          displayName: 'Device Information Charts',
          apiMethod: 'getDeviceInfoAggregate',
          required: false,
          updateMethod: 'updateDeviceInfoAggregate'
        }
      ]
    },
    {
      id: 'bootKpi',
      name: 'Boot KPI',
      priority: 4,
      sequential: false,
      panels: [
        {
          key: 'bootKpi',
          displayName: 'Boot KPI',
          apiMethod: 'getBootKpiAggregate',
          required: false,
          updateMethod: 'updateBootKpiAggregate'
        }
      ]
    },
  ]
};

/**
 * Helper function to generate initial loading states from configuration
 * This ensures loading states are always in sync with LOADING_CONFIG
 */
const getInitialLoadingStates = () => {
  const states = {};
  LOADING_CONFIG.phases.forEach(phase => {
    phase.panels.forEach(panel => {
      states[panel.key] = false;
    });
  });
  return states;
};

/**
 * Helper function to check if a section (or multiple sections) is loading
 * This ensures UI components don't need to hardcode panel keys
 * @param {string|string[]} sectionIds - Single section ID or array of section IDs
 * @param {object} loadingStates - Current loading states object
 * @returns {boolean} - True if any panel in the specified section(s) is loading
 */
export const isSectionLoading = (sectionIds, loadingStates) => {
  const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
  
  for (const sectionId of ids) {
    const section = LOADING_CONFIG.phases.find(p => p.id === sectionId);
    if (section) {
      // Check if ANY panel in this section is loading
      const isLoading = section.panels.some(panel => loadingStates[panel.key]);
      if (isLoading) return true;
    }
  }
  
  return false;
};

/**
 * Helper function to normalize time range for API calls
 */
const normalizeTimeRange = (timeRange) => {
  // If it's a string (preset), return as-is
  if (typeof timeRange === 'string') {
    return timeRange;
  }
  
  // If it's a custom range object, pass the exact custom range to API
  if (typeof timeRange === 'object' && timeRange.type === 'custom') {
    // Return the custom range object with exact timestamps
    // The API service will handle converting these to epoch timestamps
    return {
      type: 'custom',
      from: timeRange.from,
      to: timeRange.to,
      // Also include display info for caching purposes
      display: timeRange.display
    };
  }
  
  // Fallback to default
  return '24h';
};

/**
 * Custom hook for managing device data with preloading
 */
export const useDeviceData = (deviceId, timeRange = '24h') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingStates, setLoadingStates] = useState(getInitialLoadingStates());
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const abortControllerRef = useRef(null);
  const cacheRef = useRef(new Map());

  // Generate cache key
  const getCacheKey = useCallback((deviceId, timeRange) => {
    return `${deviceId}-${timeRange}`;
  }, []);

  // Update individual section data progressively
  const updateSectionData = useCallback((sectionName, sectionData) => {
    setData(prev => {
      const updated = {
        ...prev,
        [sectionName]: sectionData
      };
      
      // Cache the updated data
      const cacheKey = getCacheKey(deviceId, timeRange);
      cacheRef.current.set(cacheKey, updated);
      
      return updated;
    });
    
    // Clear loading state for this section
    setLoadingStates(prev => ({
      ...prev,
      [sectionName]: false
    }));
  }, [deviceId, timeRange, getCacheKey]);

  const updateDeviceInfoAggregate = useCallback((_, deviceInfoAggregate) => {
    setData(prev => {
      const updated = {
        ...prev,
        metadata: deviceInfoAggregate.metadata || prev?.metadata || null,
        deviceInfo: {
          licenseSN: deviceInfoAggregate.licenseSN || { labels: [], data: [], colors: [] },
          ratDistribution: deviceInfoAggregate.ratDistribution || { labels: [], data: [], colors: [] },
          flashMemoryPartitions: deviceInfoAggregate.flashMemoryPartitions || { labels: [], data: [], colors: [] },
          freshness: deviceInfoAggregate.freshness || null
        }
      };

      const cacheKey = getCacheKey(deviceId, timeRange);
      cacheRef.current.set(cacheKey, updated);
      return updated;
    });

    setLoadingStates(prev => ({ ...prev, deviceInfo: false }));
  }, [deviceId, timeRange, getCacheKey]);

  const updateDeviceHealthAggregate = useCallback((_, deviceHealthAggregate) => {
    setData(prev => {
      const updated = {
        ...prev,
        deviceHealth: {
          thermalData: deviceHealthAggregate.thermalData || { series: [] },
          flashUsage: deviceHealthAggregate.flashUsage || { series: [] },
          memoryUsage: deviceHealthAggregate.memoryUsage || { series: [] },
          cpuUsage: deviceHealthAggregate.cpuUsage || { series: [] },
          subsystemRestartEvents: deviceHealthAggregate.subsystemRestartEvents || { labels: [], data: [], colors: [] },
          subsystemRestartTimeline: deviceHealthAggregate.subsystemRestartTimeline || { eventSeries: [] },
          freshness: deviceHealthAggregate.freshness || null,
          error: deviceHealthAggregate.error || false,
          message: deviceHealthAggregate.message || null,
          details: deviceHealthAggregate.details || null
        }
      };

      const cacheKey = getCacheKey(deviceId, timeRange);
      cacheRef.current.set(cacheKey, updated);
      return updated;
    });

    setLoadingStates(prev => ({ ...prev, deviceHealth: false }));
  }, [deviceId, timeRange, getCacheKey]);

  // Update network health from single aggregate API
  const updateNetworkHealth = useCallback((_, networkHealth) => {
    setData(prev => {
      const updated = {
        ...prev,
        networkHealth
      };

      const cacheKey = getCacheKey(deviceId, timeRange);
      cacheRef.current.set(cacheKey, updated);

      return updated;
    });

    setLoadingStates(prev => ({ ...prev, networkHealth: false }));
  }, [deviceId, timeRange, getCacheKey]);

  const updateBootKpiAggregate = useCallback((_, bootKpiAggregate) => {
    setData(prev => {
      const updated = {
        ...prev,
        bootKpi: bootKpiAggregate
      };

      const cacheKey = getCacheKey(deviceId, timeRange);
      cacheRef.current.set(cacheKey, updated);

      return updated;
    });

    setLoadingStates(prev => ({ ...prev, bootKpi: false }));
  }, [deviceId, timeRange, getCacheKey]);

  // Generic panel loader based on configuration
  const loadPanel = useCallback(async (panel, deviceId, timeRange, phaseIndex, panelIndex) => {
    const { key, displayName, apiMethod, required, updateMethod } = panel;
    
    // Set loading state for this panel
    setLoadingStates(prev => ({ ...prev, [key]: true }));
    
    try {
      // Call the appropriate API method
      const normalizedTimeRange = normalizeTimeRange(timeRange);
      let result;
      
      if (deviceDataService[apiMethod]) {
        result = await deviceDataService[apiMethod](deviceId, normalizedTimeRange);
      } else {
        throw new Error(`API method ${apiMethod} not found in deviceDataService`);
      }
      
      // Call the appropriate update method
      const updateMethods = {
        updateSectionData,
        updateDeviceInfoAggregate,
        updateDeviceHealthAggregate,
        updateNetworkHealth,
        updateBootKpiAggregate
      };
      
      const updateFn = updateMethods[updateMethod];
      if (updateFn) {
        updateFn(key, result);
      } else {
        console.warn(`Update method ${updateMethod} not found, using default`);
        updateSectionData(key, result);
      }
      
      return result;
      
    } catch (err) {
      console.error(`❌ ${displayName} failed:`, err);
      setLoadingStates(prev => ({ ...prev, [key]: false }));
      
      if (required) {
        throw err; // Re-throw if required panel fails
      }
      
      if (key === 'networkHealth') {
        updateSectionData('networkHealth', {
          error: true,
          message: 'Failed to load Network Health data',
          details: err.message || 'Unknown error'
        });
      } else if (key === 'deviceInfo') {
        updateDeviceInfoAggregate('deviceInfo', {
          metadata: null,
          licenseSN: { labels: [], data: [], colors: [] },
          ratDistribution: { labels: [], data: [], colors: [] },
          flashMemoryPartitions: { labels: [], data: [], colors: [] },
          freshness: null
        });
      } else if (key === 'deviceHealth') {
        updateDeviceHealthAggregate('deviceHealth', {
          error: true,
          message: 'Failed to load Device Health data',
          details: err.message || 'Unknown error',
          thermalData: { series: [] },
          flashUsage: { series: [] },
          memoryUsage: { series: [] },
          cpuUsage: { series: [] },
          subsystemRestartEvents: { labels: [], data: [], colors: [] },
          subsystemRestartTimeline: { eventSeries: [] },
          freshness: null
        });
      } else if (key === 'bootKpi') {
        updateBootKpiAggregate('bootKpi', {
          error: true,
          message: 'Failed to load Boot KPI data',
          details: err.message || 'Unknown error',
          kpiDistribution: { labels: [], data: [], colors: [] },
          timingHistograms: {},
          freshness: null
        });
      }
      
      return null;
    }
  }, [updateSectionData, updateDeviceInfoAggregate, updateDeviceHealthAggregate, updateNetworkHealth, updateBootKpiAggregate]);

  // Generic phase loader based on configuration
  const loadPhase = useCallback(async (phase, deviceId, timeRange, phaseIndex) => {
    const { id, name, sequential, panels } = phase;
    
    try {
      if (sequential) {
        // Load panels sequentially within this phase
        for (let i = 0; i < panels.length; i++) {
          await loadPanel(panels[i], deviceId, timeRange, phaseIndex, i);
        }
      } else {
        // Load panels in parallel within this phase
        const panelPromises = panels.map((panel, panelIndex) => 
          loadPanel(panel, deviceId, timeRange, phaseIndex, panelIndex)
        );
        await Promise.allSettled(panelPromises);
      }
      
    } catch (err) {
      console.warn(`⚠️ Phase ${phaseIndex + 1} (${name}) had errors, continuing...`, err.message);
    }
  }, [loadPanel]);

  // Configuration-driven loading
  const preloadDeviceData = useCallback(async (deviceId, timeRange) => {
    if (!deviceId) {
      setData(null);
      setError(null);
      setLoading(false);
      setLoadingStates(getInitialLoadingStates());
      return;
    }

    // Cancel any existing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const cacheKey = getCacheKey(deviceId, timeRange);
    
    // Check cache first
    if (cacheRef.current.has(cacheKey)) {
      const cachedData = cacheRef.current.get(cacheKey);
      const cacheAge = Date.now() - new Date(cachedData.loadedAt).getTime();
      
      // Use cached data if less than the configured duration
      if (cacheAge < CACHE_DURATION) {
        setData(cachedData);
        setError(null);
        setLoading(false);
        
        // Generate loading states from configuration
        const cachedLoadingStates = {};
        LOADING_CONFIG.phases.forEach(phase => {
          phase.panels.forEach(panel => {
            cachedLoadingStates[panel.key] = false;
          });
        });
        setLoadingStates(cachedLoadingStates);
        return cachedData;
      }
    }

    // Initialize configuration-driven loading
    setLoading(true);
    setError(null);
    
    // Initialize data structure - no loading flags, use loadingStates instead
    const initialData = {
      metadata: null,
      deviceInfo: null,
      deviceHealth: null,
      networkHealth: null,
      bootKpi: null,
      loadedAt: new Date().toISOString(),
      timeRange
    };
    setData(initialData);
    
    // Set all panels to loading initially based on configuration
    const allLoadingStates = {};
    LOADING_CONFIG.phases.forEach(phase => {
      phase.panels.forEach(panel => {
        allLoadingStates[panel.key] = true;
      });
    });
    setLoadingStates(allLoadingStates);

    try {
      // Sort phases by priority and load concurrently.
      // Each phase/panel still keeps its own loading and error state.
      const sortedPhases = [...LOADING_CONFIG.phases].sort((a, b) => a.priority - b.priority);
      const phasePromises = sortedPhases.map((phase, index) =>
        loadPhase(phase, deviceId, timeRange, index)
      );
      await Promise.allSettled(phasePromises);

    } catch (err) {
      console.error('❌ Configuration-driven loading failed:', err);
      setError(err.message || 'Failed to load device data');
    } finally {
      setLoading(false);
    }

    // Return initial data structure
    return initialData;
  }, [getCacheKey, loadPhase]);

  // Effect to load data when deviceId, timeRange, or refreshTrigger changes
  useEffect(() => {
    if (deviceId) {
      preloadDeviceData(deviceId, timeRange);
    } else {
      setData(null);
      setError(null);
      setLoading(false);
      setLoadingStates(getInitialLoadingStates());
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [deviceId, timeRange, refreshTrigger, preloadDeviceData]);

  // Simple refresh function that reuses time range logic
  const refresh = useCallback(() => {
    if (!deviceId) return;
    
    // Clear cache to force fresh data
    const cacheKey = getCacheKey(deviceId, timeRange);
    cacheRef.current.delete(cacheKey);
    
    // Trigger the same useEffect that handles time range changes
    setRefreshTrigger(prev => prev + 1);
  }, [deviceId, timeRange, getCacheKey]);

  // Retry loading a specific section using config-driven approach
  const retrySection = useCallback((sectionKey) => {
    if (!deviceId) return;
    
    // Find the panel in configuration
    let foundPanel = null;
    let foundPhaseIndex = -1;
    let foundPanelIndex = -1;
    
    for (let phaseIndex = 0; phaseIndex < LOADING_CONFIG.phases.length; phaseIndex++) {
      const phase = LOADING_CONFIG.phases[phaseIndex];
      const panelIndex = phase.panels.findIndex(p => p.key === sectionKey);
      if (panelIndex !== -1) {
        foundPanel = phase.panels[panelIndex];
        foundPhaseIndex = phaseIndex;
        foundPanelIndex = panelIndex;
        break;
      }
    }
    
    if (!foundPanel) {
      console.error(`❌ Section ${sectionKey} not found in configuration`);
      return;
    }
    
    // Retry loading using the config-driven loadPanel function
    loadPanel(foundPanel, deviceId, timeRange, foundPhaseIndex, foundPanelIndex)
      .then(() => {})
      .catch(err => {
        console.error(`❌ ${sectionKey} retry failed:`, err);
      });
  }, [deviceId, timeRange, loadPanel]);

  // Check if any section is loading
  const isAnyLoading = Object.values(loadingStates).some(Boolean) || loading;

  // Check if device has data
  const hasData = data && data.metadata;

  return {
    data,
    loading: isAnyLoading,
    error,
    loadingStates,
    hasData,
    refresh,
    retrySection,
    preloadDeviceData
  };
};

/**
 * Hook for managing device search and validation
 */
export const useDeviceSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isValidDevice, setIsValidDevice] = useState(false);
  const [recentDevices, setRecentDevices] = useState([]);

  // Load recent devices from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentDevices');
    if (saved) {
      try {
        setRecentDevices(JSON.parse(saved));
      } catch (err) {
        console.error('Error loading recent devices:', err);
      }
    }
  }, []);

  // Validate device ID format (more flexible now)
  const validateDeviceId = useCallback((deviceId) => {
    if (!deviceId) return false;
    
    // Accept any non-empty string that's at least 3 characters
    const trimmed = deviceId.trim();
    return trimmed.length >= 3;
  }, []);

  // Handle search term change
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    setIsValidDevice(validateDeviceId(term));
  }, [validateDeviceId]);

  // Add device to recent list
  const addToRecentDevices = useCallback((deviceId, metadata = null) => {
    if (!deviceId) return;

    const deviceInfo = {
      id: deviceId,
      name: metadata?.device_info_device_make || 'Unknown Device',
      model: metadata?.device_info_device_model || '',
      lastAccessed: new Date().toISOString()
    };

    setRecentDevices(prev => {
      // Remove if already exists
      const filtered = prev.filter(d => d.id !== deviceId);
      // Add to front
      const updated = [deviceInfo, ...filtered].slice(0, 10); // Keep only 10 recent
      
      // Save to localStorage
      localStorage.setItem('recentDevices', JSON.stringify(updated));
      
      return updated;
    });
  }, []);

  // Clear recent devices
  const clearRecentDevices = useCallback(() => {
    setRecentDevices([]);
    localStorage.removeItem('recentDevices');
  }, []);

  return {
    searchTerm,
    isValidDevice,
    recentDevices,
    handleSearchChange,
    addToRecentDevices,
    clearRecentDevices,
    validateDeviceId
  };
};

/**
 * Hook for managing loading states across multiple sections
 */
export const useLoadingStates = (sections = []) => {
  const [loadingStates, setLoadingStates] = useState(
    sections.reduce((acc, section) => ({ ...acc, [section]: false }), {})
  );

  const setLoading = useCallback((section, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [section]: isLoading
    }));
  }, []);

  const setAllLoading = useCallback((isLoading) => {
    setLoadingStates(prev => 
      Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: isLoading }), {})
    );
  }, []);

  const isAnyLoading = Object.values(loadingStates).some(Boolean);
  const areAllLoading = Object.values(loadingStates).every(Boolean);

  return {
    loadingStates,
    setLoading,
    setAllLoading,
    isAnyLoading,
    areAllLoading
  };
};

export default useDeviceData;
